import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, User, Mail, Phone, LogIn } from "lucide-react";
import { toast } from "sonner";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { BrandLogo } from "@/components/BrandMark";
import { useCustomerAuth, formatApiError } from "@/context/CustomerAuthContext";
import usePageTitle from "@/hooks/usePageTitle";

const inputCls =
  "w-full px-3.5 py-3 rounded-lg border border-[var(--hk-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--hk-orange)]/50 text-[14px] placeholder:text-[var(--hk-ink-3)]";

const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen flex items-center justify-center py-16 px-6 bg-[var(--hk-ivory)] relative overflow-hidden">
    <BotanicalBackdrop variant="corners" />
    <div className="relative w-full max-w-md">
      <Link to="/" className="flex items-center justify-center gap-3 mb-8">
        <BrandLogo variant="light" markSize={46} wordSize={22} showTagline={true} taglineSize={9} testId="auth-brand-logo" />
      </Link>
      <div className="hk-card p-8 lg:p-10">
        <div className="label-eyebrow flex items-center gap-1.5">
          <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" />
          Your HerbKranti Account
        </div>
        <h1 className="serif mt-3 text-[32px] leading-[1.15] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-[14px] text-[var(--hk-ink-2)] leading-[1.75]">{subtitle}</p>}
        <div className="mt-8">{children}</div>
      </div>
      {footer && <div className="text-center mt-6 text-[13px] text-[var(--hk-ink-2)]">{footer}</div>}
    </div>
  </div>
);

/* ---------- LOGIN ---------- */
export const CustomerLoginPage = () => {
  usePageTitle("Sign In");
  const { user, loading, login } = useCustomerAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to={loc.state?.next || "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return toast.error("Please fill both fields");
    setBusy(true);
    try {
      await login({ identifier: identifier.trim(), password });
      toast.success("Welcome back to HerbKranti.");
      nav(loc.state?.next || "/account");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back."
      subtitle="Sign in to view your orders, reorder favourites, and manage your wellness routine."
      footer={<>New here? <Link to="/signup" className="text-[var(--hk-orange-deep)] hover:underline" data-testid="login-signup-link">Create an account</Link></>}
    >
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="customer-login-form">
        <label className="block">
          <div className="label-eyebrow !text-[10px] mb-1.5">Email · Mobile · Username</div>
          <input
            className={inputCls}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@example.com  ·  +91…  ·  username"
            autoComplete="username"
            data-testid="login-identifier-input"
            required
          />
        </label>
        <label className="block">
          <div className="label-eyebrow !text-[10px] mb-1.5">Password</div>
          <div className="relative">
            <input
              className={inputCls + " pr-11"}
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="login-password-input"
              required
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[var(--hk-ink-3)] hover:text-[var(--hk-orange-deep)]" aria-label="Toggle password visibility">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>

        <div className="flex items-center justify-between text-[12px]">
          <label className="flex items-center gap-2 text-[var(--hk-ink-2)]">
            <input type="checkbox" className="accent-[var(--hk-orange)]" defaultChecked />
            Remember me
          </label>
          <Link to="/forgot-password" className="text-[var(--hk-orange-deep)] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
        </div>

        <button type="submit" disabled={busy} className="hk-btn-primary w-full !py-3 justify-center" data-testid="login-submit-btn">
          <LogIn size={15} /> {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
};

/* ---------- SIGNUP ---------- */
export const CustomerSignupPage = () => {
  usePageTitle("Create Account");
  const { user, loading, signup } = useCustomerAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!loading && user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.password) return toast.error("Name and password are required");
    if (!form.email && !form.mobile) return toast.error("Please provide an email or mobile number");
    setBusy(true);
    try {
      await signup(form);
      toast.success("Welcome to HerbKranti. Your account is ready.");
      nav("/account");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Create your account."
      subtitle="One account for orders, reorder, refill reminders and your saved addresses."
      footer={<>Already have an account? <Link to="/login" className="text-[var(--hk-orange-deep)] hover:underline" data-testid="signup-login-link">Sign in</Link></>}
    >
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="customer-signup-form">
        <label className="block">
          <div className="label-eyebrow !text-[10px] mb-1.5">Full name *</div>
          <div className="relative">
            <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]" />
            <input className={inputCls + " pl-10"} value={form.name} onChange={set("name")} required data-testid="signup-name-input" />
          </div>
        </label>

        <label className="block">
          <div className="label-eyebrow !text-[10px] mb-1.5">Email address</div>
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]" />
            <input className={inputCls + " pl-10"} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="signup-email-input" />
          </div>
        </label>

        <label className="block">
          <div className="label-eyebrow !text-[10px] mb-1.5">Mobile number</div>
          <div className="relative">
            <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]" />
            <input className={inputCls + " pl-10"} value={form.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" data-testid="signup-mobile-input" />
          </div>
          <div className="mt-1 text-[11px] text-[var(--hk-ink-3)]">Provide at least one — email or mobile.</div>
        </label>

        <label className="block">
          <div className="label-eyebrow !text-[10px] mb-1.5">Password *</div>
          <div className="relative">
            <input
              className={inputCls + " pr-11"}
              type={showPw ? "text" : "password"}
              value={form.password}
              onChange={set("password")}
              autoComplete="new-password"
              data-testid="signup-password-input"
              required
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[var(--hk-ink-3)] hover:text-[var(--hk-orange-deep)]">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="mt-1 text-[11px] text-[var(--hk-ink-3)]">Minimum 8 characters with at least one letter and one number.</div>
        </label>

        <button type="submit" disabled={busy} className="hk-btn-primary w-full !py-3 justify-center" data-testid="signup-submit-btn">
          {busy ? "Creating account…" : "Create Account"} <ArrowRight size={15} />
        </button>
      </form>
    </AuthShell>
  );
};

/* ---------- FORGOT PASSWORD ---------- */
export const CustomerForgotPasswordPage = () => {
  usePageTitle("Reset Password");
  const [identifier, setIdentifier] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { customerApi } = await import("@/context/CustomerAuthContext");
      await customerApi.post("/customer/forgot-password", { identifier: identifier.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Request failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell
      title="Forgot password?"
      subtitle="Enter your email or mobile and we'll send you a secure reset link."
      footer={<>Remembered it? <Link to="/login" className="text-[var(--hk-orange-deep)] hover:underline">Back to sign in</Link></>}
    >
      {sent ? (
        <div className="text-[14px] leading-[1.85] text-[var(--hk-ink-2)]">
          If an account matches, a reset link has been sent. Please check your inbox or reach out on WhatsApp if you need assistance.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="customer-forgot-form">
          <label className="block">
            <div className="label-eyebrow !text-[10px] mb-1.5">Email · Mobile · Username</div>
            <input className={inputCls} value={identifier} onChange={(e) => setIdentifier(e.target.value)} required data-testid="forgot-identifier-input" />
          </label>
          <button type="submit" disabled={busy} className="hk-btn-primary w-full !py-3 justify-center">
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
};
